"use client";

import React, { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import PostHogInit from "@/app/posthog-init";
import { DatadogRumProvider } from "@/components/DatadogRumProvider";

// Global error handler component
function ErrorHandler() {
  useEffect(() => {
    // This will capture unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.log("Capturing unhandled rejection:", event.reason);
      posthog.captureException(event.reason);
    };

    // This will capture regular errors
    const handleError = (event: ErrorEvent) => {
      console.log("Capturing error:", event.error);
      posthog.captureException(event.error);
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Clean up
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        // With SSR, we usually want to set some default staleTime
                        // above 0 to avoid refetching immediately on the client
                        staleTime: 60 * 1000,
                        refetchOnWindowFocus: false,
                    },
                },
            })
    );

    return (
        <QueryClientProvider client={queryClient}>
            <DatadogRumProvider>
                <PostHogInit />
                <ErrorHandler />
                {children}
            </DatadogRumProvider>
        </QueryClientProvider>
    );
} 